import React, { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { MoreHorizontal, Check, AlertTriangle, ArrowUpDown, Trash2 } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import fetchWithAuth from "../fetchWithAuth";
import { useBulkRowSelection, SelectionCheckbox, BulkDeleteBar } from "./BulkRowSelection";

// Импортируем Input для поля ввода числа
import { Input } from "./ui/input";

function PopoverMenu({ page, projectId, setLocalPages, setError, openDeleteDialog }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const closeTimer = React.useRef(null);

  // Состояния для поп-апа буста
  const [isBoostDialogOpen, setIsBoostDialogOpen] = React.useState(false);
  const [boostValue, setBoostValue] = React.useState(page.boostCount || 1);
  const [boostError, setBoostError] = React.useState("");

  // Состояния для поп-апа изменения доп. слова
  const [isDopSlovoDialogOpen, setIsDopSlovoDialogOpen] = React.useState(false);
  const [dopSlovoValue, setDopSlovoValue] = React.useState(page.dopSlovo || "");
  const [dopSlovoError, setDopSlovoError] = React.useState("");

  // Состояния для поп-апа изменения домена
  const [isDomainDialogOpen, setIsDomainDialogOpen] = React.useState(false);
  const [domainValue, setDomainValue] = React.useState(page.domainCustom || "");
  const [domainError, setDomainError] = React.useState("");

  const handleMouseEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const handleMouseLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setIsOpen(false), 1000);
  };

  // Включить буст
  const handleEnableBoost = () => {
    setBoostValue(page.boostCount || 1);
    setBoostError("");
    setIsBoostDialogOpen(true);
    setIsOpen(false);
  };

  // Открыть попап изменения доп. слова
  const handleEditDopSlovo = () => {
    setDopSlovoValue(page.dopSlovo || "");
    setDopSlovoError("");
    setIsDopSlovoDialogOpen(true);
    setIsOpen(false);
  };

  // Открыть попап изменения домена
  const handleEditDomain = () => {
    setDomainValue(page.domainCustom || "");
    setDomainError("");
    setIsDomainDialogOpen(true);
    setIsOpen(false);
  };

  // Сбросить доп. слово
  const handleResetDopSlovo = async () => {
    try {
      await fetchWithAuth(`/api/project/${projectId}/promoted/${page.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dopSlovo: null }),
      });
      const updatedDataRes = await fetchWithAuth(`/api/project/${projectId}`);
      const updatedData = await updatedDataRes.json();
      setLocalPages(updatedData.promotedPages || []);
      setIsOpen(false);
    } catch (e) {
      setError("Ошибка при сбросе доп. слова");
      setIsOpen(false);
    }
  };

  // Подтвердить изменение доп. слова
  const handleDopSlovoConfirm = async () => {
    if (dopSlovoValue.trim().length === 0) {
      setDopSlovoError("Введите доп. слово");
      return;
    }
    try {
      await fetchWithAuth(`/api/project/${projectId}/promoted/${page.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dopSlovo: dopSlovoValue.trim() }),
      });
      const updatedDataRes = await fetchWithAuth(`/api/project/${projectId}`);
      const updatedData = await updatedDataRes.json();
      setLocalPages(updatedData.promotedPages || []);
      setIsDopSlovoDialogOpen(false);
    } catch (e) {
      setDopSlovoError("Ошибка при изменении доп. слова");
    }
  };

  // Сбросить домен
  const handleResetDomain = async () => {
    try {
      await fetchWithAuth(`/api/project/${projectId}/promoted/${page.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domainCustom: null }),
      });
      const updatedDataRes = await fetchWithAuth(`/api/project/${projectId}`);
      const updatedData = await updatedDataRes.json();
      setLocalPages(updatedData.promotedPages || []);
      setIsOpen(false);
    } catch (e) {
      setError("Ошибка при сбросе домена");
      setIsOpen(false);
    }
  };

  // Подтвердить изменение домена
  const handleDomainConfirm = async () => {
    if (domainValue.trim().length === 0) {
      setDomainError("Введите домен");
      return;
    }
    try {
      await fetchWithAuth(`/api/project/${projectId}/promoted/${page.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domainCustom: domainValue.trim() }),
      });
      const updatedDataRes = await fetchWithAuth(`/api/project/${projectId}`);
      const updatedData = await updatedDataRes.json();
      setLocalPages(updatedData.promotedPages || []);
      setIsDomainDialogOpen(false);
    } catch (e) {
      setDomainError("Ошибка при изменении домена");
    }
  };

  // Отключить буст
  const handleDisableBoost = async () => {
    try {
      await fetchWithAuth(`/api/project/${projectId}/promoted/${page.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          boost_count: null,
          boostAt: null
        }),
      });
      const updatedDataRes = await fetchWithAuth(`/api/project/${projectId}`);
      const updatedData = await updatedDataRes.json();
      setLocalPages(updatedData.promotedPages || []);
      setIsOpen(false);
    } catch (e) {
      setError("Ошибка при отключении буста");
      setIsOpen(false);
    }
  };

  // Подтвердить буст
  const handleBoostConfirm = async () => {
    if (!boostValue || isNaN(boostValue) || boostValue < 1 || boostValue > 1000) {
      setBoostError("Введите число от 1 до 1000");
      return;
    }
    try {
      // Буст включается на следующий день
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      await fetchWithAuth(`/api/project/${projectId}/promoted/${page.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          boost_count: Number(boostValue),
          boostAt: tomorrow.toISOString()
        }),
      });
      const updatedDataRes = await fetchWithAuth(`/api/project/${projectId}`);
      const updatedData = await updatedDataRes.json();
      setLocalPages(updatedData.promotedPages || []);
      setIsBoostDialogOpen(false);
    } catch (e) {
      setBoostError("Ошибка при включении буста");
    }
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ display: "inline-block" }}
    >
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            onClick={() => setIsOpen((v) => !v)}
            tabIndex={0}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-56 text-sm">
          <div className="flex flex-col">
            {/* Изменить доп. слово */}
            <Button
              variant="ghost"
              className="justify-start w-full rounded-none text-left text-sm"
              onClick={handleEditDopSlovo}
            >
              Изменить доп. слово
            </Button>
            {/* Сбросить доп. слово */}
            {page.dopSlovo != null && (
              <Button
                variant="ghost"
                className="justify-start w-full rounded-none text-left text-sm"
                onClick={handleResetDopSlovo}
              >
                Сбросить доп. слово
              </Button>
            )}
            <Button
              variant="ghost"
              className="justify-start w-full rounded-none text-left text-sm"
              onClick={handleEditDomain}
            >
              Изменить домен
            </Button>
            {page.domainCustom != null && (
              <Button
                variant="ghost"
                className="justify-start w-full rounded-none text-left text-sm"
                onClick={handleResetDomain}
              >
                Сбросить домен
              </Button>
            )}
            {!page.isActive && (
              <Button
                variant="ghost"
                className="justify-start w-full rounded-none text-left text-sm"
                onClick={async () => {
                  try {
                    await fetchWithAuth(`/api/project/${projectId}/promoted/${page.id}`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        isActive: true,
                        stagnationCount: 0,
                        stoppedAt: null
                      }),
                    });
                    const updatedDataRes = await fetchWithAuth(`/api/project/${projectId}`);
                    const updatedData = await updatedDataRes.json();
                    setLocalPages(updatedData.promotedPages || []);
                    setIsOpen(false);
                  } catch (e) {
                    setError("Ошибка при активации");
                    setIsOpen(false);
                  }
                }}
              >
                Активировать
              </Button>
            )}
            {page.isActive && (
              <Button
                variant="ghost"
                className="justify-start w-full rounded-none text-left text-sm"
                onClick={async () => {
                  try {
                    await fetchWithAuth(`/api/project/${projectId}/promoted/${page.id}`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        isActive: false,
                        stoppedAt: new Date().toISOString(),
                        boost_count: null,
                        boostAt: null
                      }),
                    });
                    const updatedDataRes = await fetchWithAuth(`/api/project/${projectId}`);
                    const updatedData = await updatedDataRes.json();
                    setLocalPages(updatedData.promotedPages || []);
                    setIsOpen(false);
                  } catch (e) {
                    setError("Ошибка при отключении");
                    setIsOpen(false);
                  }
                }}
              >
                Отключить
              </Button>
            )}
            {/* BOOST MENU ITEMS */}
            {page.isActive && page.boostCount == null && (
              <Button
                variant="ghost"
                className="justify-start w-full rounded-none text-left text-sm"
                onClick={handleEnableBoost}
              >
                Включить буст
              </Button>
            )}
            {page.isActive && page.boostCount != null && (
              <Button
                variant="ghost"
                className="justify-start w-full rounded-none text-left text-sm"
                onClick={handleDisableBoost}
              >
                Отключить буст
              </Button>
            )}
            <Button
              variant="ghost"
              className="justify-start w-full text-red-600 hover:bg-red-50 rounded-none text-left text-sm"
              onClick={() => {
                openDeleteDialog(page);
                setIsOpen(false);
              }}
            >
              Удалить
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* BOOST DIALOG */}
      <Dialog open={isBoostDialogOpen} onOpenChange={setIsBoostDialogOpen}>
        <DialogContent className="sm:max-w-[425px] pointer-events-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Boost для страницы
            </DialogTitle>
            <DialogDescription>
              Введите количество дополнительных кликов в сутки
              <div className="mt-2">
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  value={boostValue}
                  onChange={e => setBoostValue(e.target.value)}
                  className="w-full"
                  autoFocus
                />
                {boostError && (
                  <div className="text-red-600 text-sm mt-1">{boostError}</div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsBoostDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button
              onClick={handleBoostConfirm}
            >
              Применить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DOP SLOVO DIALOG */}
      <Dialog open={isDopSlovoDialogOpen} onOpenChange={setIsDopSlovoDialogOpen}>
        <DialogContent className="sm:max-w-[425px] pointer-events-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Изменить доп. слово
            </DialogTitle>
            <DialogDescription>
              Введите новое доп. слово для страницы
              <div className="mt-2">
                <Input
                  type="text"
                  value={dopSlovoValue}
                  onChange={e => setDopSlovoValue(e.target.value)}
                  className="w-full"
                  autoFocus
                />
                {dopSlovoError && (
                  <div className="text-red-600 text-sm mt-1">{dopSlovoError}</div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDopSlovoDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button
              onClick={handleDopSlovoConfirm}
            >
              Применить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DOMAIN DIALOG */}
      <Dialog open={isDomainDialogOpen} onOpenChange={setIsDomainDialogOpen}>
        <DialogContent className="sm:max-w-[425px] pointer-events-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Изменить домен
            </DialogTitle>
            <DialogDescription>
              Введите новый домен для подстановки в задания
              <div className="mt-2">
                <Input
                  type="text"
                  value={domainValue}
                  onChange={e => setDomainValue(e.target.value)}
                  className="w-full"
                  autoFocus
                />
                {domainError && (
                  <div className="text-red-600 text-sm mt-1">{domainError}</div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDomainDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button
              onClick={handleDomainConfirm}
            >
              Применить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Вспомогательная функция для извлечения домена из URL
function extractDomain(url) {
  try {
    const normalized = url.startsWith("http") ? url : "http://" + url;
    return new URL(normalized).hostname.toLowerCase();
  } catch {
    return null;
  }
}

export default function PromotedPagesTable({ projectId, pages = [], blacklistPages = [], onChange, onBulkChange }) {
  const [newUrl, setNewUrl] = useState("");
  const [error, setError] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [localPages, setLocalPages] = useState(pages);
  const selection = useBulkRowSelection(localPages, setLocalPages, { projectId, resource: "promoted", onChange: onBulkChange });
  const [sorting, setSorting] = useState({ column: "createdAt", direction: "desc" });
  const [isDeleteAllDialogOpen, setIsDeleteAllDialogOpen] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  // Обновляем localPages при изменении props.pages
  React.useEffect(() => {
    setLocalPages(pages);
  }, [pages]);

  // Набор доменов из списка вытесняемых страниц
  const blacklistDomains = React.useMemo(() => {
    const set = new Set();
    for (const bp of blacklistPages) {
      const domain = extractDomain(bp.blackUrl);
      if (domain) set.add(domain);
    }
    return set;
  }, [blacklistPages]);

  // Вычислить текст предупреждения для продвигаемой страницы
  const getBlacklistWarning = (page) => {
    const domain = extractDomain(page.url);
    if (!domain || !blacklistDomains.has(domain)) return null;
    const hasDopSlovo = page.dopSlovo != null && page.dopSlovo !== "";
    const hasDomainCustom = page.domainCustom != null && page.domainCustom !== "";
    if (hasDopSlovo && hasDomainCustom) return null;
    const base = "У этого сайта есть страница в списке на вытеснение.";
    if (!hasDopSlovo && !hasDomainCustom) {
      return `${base} Задайте кастомные Доп. слово и Домен.`;
    }
    if (!hasDopSlovo) {
      return `${base} Задайте кастомное Доп. слово.`;
    }
    return `${base} Задайте кастомный Домен.`;
  };

  // Сортировка
  const handleSort = (column) => {
    setSorting(prev => ({
      column,
      direction: prev.column === column && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const getSortedPages = () => {
    const sorted = [...localPages];
    switch (sorting.column) {
      case "url":
        sorted.sort((a, b) => {
          if (!a.url) return 1;
          if (!b.url) return -1;
          if (a.url < b.url) return sorting.direction === "asc" ? -1 : 1;
          if (a.url > b.url) return sorting.direction === "asc" ? 1 : -1;
          return 0;
        });
        break;
      case "currAvgPosition":
        sorted.sort((a, b) => {
          const av = a.currAvgPosition ?? Number.POSITIVE_INFINITY;
          const bv = b.currAvgPosition ?? Number.POSITIVE_INFINITY;
          return sorting.direction === "asc" ? av - bv : bv - av;
        });
        break;
      case "currTop20Count":
        sorted.sort((a, b) => {
          const av = a.currTop20Count ?? 0;
          const bv = b.currTop20Count ?? 0;
          return sorting.direction === "asc" ? av - bv : bv - av;
        });
        break;
      case "createdAt":
        sorted.sort((a, b) => {
          const av = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bv = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return sorting.direction === "asc" ? av - bv : bv - av;
        });
        break;
      case "stoppedAt":
        sorted.sort((a, b) => {
          const av = a.stoppedAt ? new Date(a.stoppedAt).getTime() : 0;
          const bv = b.stoppedAt ? new Date(b.stoppedAt).getTime() : 0;
          return sorting.direction === "asc" ? av - bv : bv - av;
        });
        break;
      case "isActive":
        sorted.sort((a, b) => {
          const av = a.isActive ? 1 : 0;
          const bv = b.isActive ? 1 : 0;
          return sorting.direction === "asc" ? av - bv : bv - av;
        });
        break;
      // index сортировка убрана
      default:
        break;
    }
    return sorted;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError("");
    if (!newUrl) {
      setError("Введите URL");
      return;
    }
    try {
      // Разделяем строки и обрабатываем каждый URL
      const urls = newUrl.split('\n').filter(url => url.trim());
      
      if (urls.length === 0) {
        setError("Введите URL");
        return;
      }
      
      // Добавляем каждый URL
      for (const url of urls) {
        const res = await fetchWithAuth(`/api/project/${projectId}/promoted`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ urls: url.trim() }),
        });
        
        if (!res.ok) {
          const data = await res.json();
          setError(`Ошибка при добавлении URL ${url}: ${data.error || "Ошибка при добавлении"}`);
          return;
        }
      }
      
      // Получаем новые данные и обновляем локальное состояние вместо вызова onChange
      const updatedDataRes = await fetchWithAuth(`/api/project/${projectId}`);
      const updatedData = await updatedDataRes.json();
      setLocalPages(updatedData.promotedPages || []);
      
      setNewUrl("");
      // Не вызываем onChange, чтобы избежать перезагрузки страницы
      // onChange && onChange();
    } catch {
      setError("Ошибка при добавлении");
    }
  };

  const openDeleteDialog = (page) => {
    setPageToDelete(page);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    if (!pageToDelete) return;
    
    // Обновляем локальное состояние без перезагрузки всей страницы
    setLocalPages(prevPages => prevPages.filter(p => p.id !== pageToDelete.id));
    
    // Закрываем диалог
    setIsDeleteDialogOpen(false);
    
    // Выполняем удаление на сервере
    fetchWithAuth(`/api/project/${projectId}/promoted/${pageToDelete.id}`, {
      method: "DELETE",
    })
    .then(response => {
      if (!response.ok) {
        // Проверяем Content-Type ответа
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          return response.json().then(data => {
            throw new Error(data.error || "Ошибка при удалении");
          });
        } else {
          throw new Error("Ошибка при удалении");
        }
      }
      
      // Не уведомляем родительский компонент об изменении, чтобы не перезагружать страницу
      // onChange && onChange();
      setPageToDelete(null);
    })
    .catch(error => {
      console.error("Ошибка при удалении:", error);
      setError(error.message || "Ошибка при удалении");
    });
  };

  // Удалить все продвигаемые страницы
  const handleDeleteAll = async () => {
    setIsDeletingAll(true);
    try {
      const res = await fetchWithAuth(`/api/project/${projectId}/promoted`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Ошибка при удалении всех страниц");
        return;
      }
      setLocalPages([]);
      setIsDeleteAllDialogOpen(false);
    } catch {
      setError("Ошибка при удалении всех страниц");
    } finally {
      setIsDeletingAll(false);
    }
  };

  // Форматирование даты: dd.mm.yyyy
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  return (
    <div className="space-y-4">
      <CardHeader className="px-0 pt-0">
        <CardTitle>Продвигаемые страницы</CardTitle>
      </CardHeader>
      
      <form onSubmit={handleAdd} className="space-y-4">
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <Textarea
              id="newUrl"
              placeholder="Новые URL (каждая строка — отдельный URL)"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <Button type="submit">Добавить</Button>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
      </form>
      
      <BulkDeleteBar selection={selection} label="Продвигаемые страницы" />
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[44px]"><SelectionCheckbox checked={selection.all} partial={selection.partial} onChange={selection.toggleAll} disabled={selection.busy || !localPages.length} label="Выбрать все продвигаемые страницы" /></TableHead>
              <TableHead className="w-[50px]">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                  title="Удалить все страницы"
                  onClick={() => setIsDeleteAllDialogOpen(true)}
                  disabled={localPages.length === 0}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="w-[50px]">#</TableHead>
              <TableHead className="w-[120px] text-center">
                <Button
                  variant="ghost"
                  onClick={() => handleSort("isActive")}
                  className="flex items-center gap-1 font-medium justify-center"
                >
                  Статус
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("url")}
                  className="flex items-center gap-1 font-medium"
                >
                  URL
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="w-[120px] text-center">
                <Button
                  variant="ghost"
                  onClick={() => handleSort("currAvgPosition")}
                  className="flex items-center gap-1 font-medium justify-center"
                >
                  Средняя позиция
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="w-[150px] text-center">
                <Button
                  variant="ghost"
                  onClick={() => handleSort("currTop20Count")}
                  className="flex items-center gap-1 font-medium justify-center"
                >
                  Повторов в топ-20
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="w-[140px] text-center">
                Доп. слово
              </TableHead>
              <TableHead className="w-[140px] text-center">
                Домен
              </TableHead>
              <TableHead className="w-[150px] text-center">
                <Button
                  variant="ghost"
                  onClick={() => handleSort("createdAt")}
                  className="flex items-center gap-1 font-medium justify-center"
                >
                  Дата добавления
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="w-[150px] text-center">
                <Button
                  variant="ghost"
                  onClick={() => handleSort("stoppedAt")}
                  className="flex items-center gap-1 font-medium justify-center"
                >
                  Дата остановки
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
{getSortedPages().map((page, idx) => (
  <TableRow key={page.id}>
    <TableCell><SelectionCheckbox checked={selection.selected.has(page.id)} onChange={() => selection.toggle(page.id)} disabled={selection.busy} label={`Выбрать продвигаемую страницу ${page.url}`} /></TableCell>
    <TableCell className="w-[50px]">
      <PopoverMenu
        page={page}
        idx={idx}
        projectId={projectId}
        setLocalPages={setLocalPages}
        setError={setError}
        openDeleteDialog={openDeleteDialog}
      />
    </TableCell>
    <TableCell className="w-[50px]">{idx + 1}</TableCell>
    <TableCell className="w-[120px] text-center">
      <div className="flex flex-col items-center gap-1">
        {page.isActive ? (
          <>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1 w-[90px] justify-center">
              <Check className="h-3 w-3" />
              Active
            </Badge>
            {/* BOOST BADGE */}
            {page.boostCount != null && (
              <Badge
                variant="outline"
                className="bg-yellow-200 text-yellow-900 border-yellow-400 flex flex-row items-center w-fit px-2 py-1"
                style={{ backgroundColor: "#FFD700", color: "#7c5a00", borderColor: "#FFD700" }}
              >
                {page.boostAt && (
                  <div className="text-xs">
                    {formatDate(page.boostAt)}
                  </div>
                )}
                <div> +{page.boostCount} </div>
              </Badge>
            )}
          </>
        ) : (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 w-[90px] justify-center flex items-center">
            Disabled
          </Badge>
        )}
        {/* BLACKLIST WARNING BADGE */}
        {(() => {
          const warning = getBlacklistWarning(page);
          if (!warning) return null;
          return (
            <Badge
              title={warning}
              className="cursor-help w-[22px] h-[22px] p-0 flex items-center justify-center text-xs font-bold rounded-full border-0"
              style={{ backgroundColor: "#ef4444", color: "#fff", minWidth: "22px", minHeight: "22px" }}
            >
              !
            </Badge>
          );
        })()}
      </div>
    </TableCell>
    <TableCell style={{ width: 500, maxWidth: 500, minWidth: 500, overflow: "hidden", padding: 0, verticalAlign: "middle" }}>
      <a 
        href={page.url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          wordBreak: "break-all",
          width: "500px",
          maxWidth: "500px",
          minWidth: "500px"
        }}
      >
        {page.url}
      </a>
    </TableCell>
    <TableCell className="w-[120px] text-center">{page.currAvgPosition || "-"}</TableCell>
    <TableCell className="w-[120px] text-center">{page.currTop20Count || "-"}</TableCell>
    <TableCell className="w-[120px] text-center">
      {page.dopSlovo
        ? page.dopSlovo
        : (() => {
            try {
              const urlObj = new URL(page.url.startsWith('http') ? page.url : 'http://' + page.url);
              const host = urlObj.hostname;
              const parts = host.split('.');
              return parts.length > 1 ? parts[parts.length - 2] : host;
            } catch {
              return "-";
            }
          })()
      }
    </TableCell>
    <TableCell className="w-[140px] text-center">
      {page.domainCustom || page.domain || "-"}
    </TableCell>
    <TableCell className="w-[120px] text-center">{formatDate(page.createdAt)}</TableCell>
    <TableCell className="w-[120px] text-center">{formatDate(page.stoppedAt)}</TableCell>
  </TableRow>
))}
            {localPages.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-4 text-gray-500">
                  Нет продвигаемых страниц
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Диалог подтверждения удаления всех */}
      <Dialog open={isDeleteAllDialogOpen} onOpenChange={setIsDeleteAllDialogOpen}>
        <DialogContent className="sm:max-w-[425px] pointer-events-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Удалить все продвигаемые страницы
            </DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите удалить <strong>все</strong> продвигаемые страницы этого проекта ({localPages.length} шт.)?
              <br />
              <span className="text-red-600">Это действие необратимо.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteAllDialogOpen(false)}
              disabled={isDeletingAll}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAll}
              disabled={isDeletingAll}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeletingAll ? "Удаление..." : "Удалить все"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px] pointer-events-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Подтверждение удаления
            </DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите удалить эту страницу?
              {pageToDelete && (
                <div className="mt-2 p-2 bg-gray-50 rounded border text-sm break-all">
                  {pageToDelete.url}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? "Удаление..." : "Удалить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
